import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { CalendarOutlined, FileDoneOutlined, MessageOutlined, TagOutlined } from "@ant-design/icons";
import { Divider, Form, Input, Radio, Select, Tree, Typography } from "antd";
import { type FormInstance } from "antd/lib/form";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import orderBy from "lodash.orderby";
import { useTranslation } from "react-i18next";

import { programAutomationActions } from "@fitness-app/app-store";
import { TaskType } from "@fitness-app/data-models/entities/Automation";
import { TemplateType } from "@fitness-app/data-models/entities/AutomationTemplate";
import { type ProgramAutomation } from "@fitness-app/data-models/entities/ProgramAutomation";
import { createAutomationSchedule } from "@fitness-app/utils/src/programAutomation/createAutomationSchedule";

import { getMinutesWithLeadingZero } from "~/modules/Automation/utils/getMinutesWithLeadingZero";
import { type ISurveyQuestionAutomationForm } from "~/modules/SurveysTemplates/SurveyDetails/SurveyQuestionAutomationForm";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

interface SurveyAutomationChatMessageFormProps {
  parentFieldName: string | number;
  path: (string | number)[];
  formController?: FormInstance<ISurveyQuestionAutomationForm>;
}

dayjs.extend(isoWeek);

const SEPARATOR = "_";

const SurveyAutomationProgramAutomationForm = ({
  parentFieldName,
  formController,
  path,
}: SurveyAutomationChatMessageFormProps): React.ReactElement => {
  const { t } = useTranslation(["automation", "common", "products", "surveys"]);
  const dispatch = useAppDispatch();
  const [selectedAutomation, setAutomation] = useState<null | ProgramAutomation | undefined>();
  const watchedFirstDay = Form.useWatch([...path, "firstDay"], formController) as
    | "surveyAnswerDate"
    | "nextWeekStart"
    | undefined;
  const watchedProgramAutomationId = Form.useWatch([...path, "programAutomationId"], formController) as
    | string
    | undefined;

  const { data } = useAppSelector((store) => store.programAutomation);
  const selectedFromModel = useRef(false);

  useEffect(() => {
    void dispatch(programAutomationActions.fetchProgramAutomations());
  }, []);

  useEffect(() => {
    if (watchedProgramAutomationId && !selectedFromModel.current) {
      onAutomationSelect(watchedProgramAutomationId);
    }
  }, [watchedProgramAutomationId]);

  const onAutomationSelect = async (value: string) => {
    selectedFromModel.current = true;
    const automation = await dispatch(programAutomationActions.fetchProgramAutomation(value)).unwrap();
    setAutomation(automation);
  };

  const startDate = watchedFirstDay === "nextWeekStart" ? dayjs().add(1, "week").startOf("isoWeek") : dayjs();

  const schedule = useMemo(
    () => createAutomationSchedule(selectedAutomation, startDate),
    [selectedAutomation, startDate],
  );

  const treeData = useMemo(
    () =>
      schedule.map((item) => ({
        title: item.dayDate,
        key: item.day.id,
        icon: <CalendarOutlined />,
        children: orderBy(item.day.items, ["sentTime.hours", "sentTime.minutes"]).map((dayItem) => ({
          title: `${dayItem.sentTime.hours}:${getMinutesWithLeadingZero(dayItem.sentTime.minutes)} - ${
            "name" in dayItem
              ? dayItem.name
              : dayItem.type === TaskType.FitnessApp
                ? `${t(`common:fitnessAppTask.${dayItem.action.type}`)} - ${dayItem.action.tags.join(", ")}`
                : dayItem.type === TaskType.TeamMemberTask
                  ? `${dayItem.title}`
                  : `${t(`common:emailClientTask.${dayItem.action.type}`)} - ${
                      "emailLists" in dayItem.action
                        ? dayItem.action.emailLists.map((item: { client: string }) => `${item.client}`).join(", ")
                        : dayItem.action.emailClients
                            .map((item: { client: string; account: string }) => `${item.client} - ${item.account}`)
                            .join(", ")
                    }`
          }`,
          icon:
            dayItem.type === TemplateType.ScheduledMessages ? (
              <MessageOutlined />
            ) : dayItem.type === TemplateType.ScheduledSurvey ? (
              <FileDoneOutlined />
            ) : (
              <TagOutlined />
            ),
          key: `${item.day.id}${SEPARATOR}${dayItem.id}`,
          children:
            dayItem.type === TemplateType.ScheduledMessages
              ? dayItem.messages.map((message, i) => ({
                  title: `- ${message}`,
                  key: `${item.day.id}-${dayItem.id}-${i}`,
                  checkable: false,
                }))
              : [],
        })),
      })),
    [schedule],
  );

  return (
    <>
      <Form.Item name="id" hidden>
        <Input />
      </Form.Item>

      <Form.Item
        name={[parentFieldName, "programAutomationId"]}
        label={t("automationSchedule.selectSchedule")}
        rules={[
          {
            required: true,
            message: t<string>("common:validationErrors.fieldIsRequired"),
          },
        ]}
      >
        <Select
          showSearch
          onSelect={onAutomationSelect}
          placeholder={t("automationSchedule.selectSchedulePlaceholder")}
          filterOption={(inputValue, option) => {
            if (option && "label" in option) {
              return String(option?.label).toUpperCase().indexOf(inputValue?.toUpperCase()) !== -1;
            }

            return false;
          }}
          options={data.map((automation) => ({
            value: automation.id,
            label: automation.name,
          }))}
        />
      </Form.Item>

      <Form.Item
        name={[parentFieldName, "firstDay"]}
        label={t("automationSchedule.firstDay")}
        rules={[
          {
            required: true,
            message: t<string>("common:validationErrors.fieldIsRequired"),
          },
        ]}
      >
        <Radio.Group size="small">
          <Radio value="surveyAnswerDate">W dniu odpowiedzi na ankietę</Radio>
          <Radio value="nextWeekStart">Od następnego tygodnia (od poniedziałku)</Radio>
        </Radio.Group>
      </Form.Item>

      {treeData?.length > 0 && (
        <div className="mt-4 space-y-2">
          <Divider orientation="center">Harmonogram zadań</Divider>

          <Typography.Text type="secondary" className="text-sm">
            Zakładając, że odpowiedź na ankietę zostanie udzielona w dniu {dayjs().format("DD.MM.YYYY")}
          </Typography.Text>

          <Tree checkable={false} showIcon selectable={false} treeData={treeData} />
        </div>
      )}
    </>
  );
};

export default memo(SurveyAutomationProgramAutomationForm);
